import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchUserSavedDesignsData,
  updateUserSavedDesignsData,
  deleteUserSavedDesignsData,
} from "./designEditorAPI";

const initialState = {
  status: "idle",
  error: null,
  data: {},
  user_saved_designs: [],
  isEditorLive: false,
  selectedDesignId: null,
  selectedDesignData: null,
  editorContent: null,
  userDesignDBNode: "graph-designs",
  defaultTemplatesDBNode: "default-prod/landing-page-templates",
  cfdefaultdesigntemplates: [],
  websiteURL: "",
  cfDefalutSettings: {},
  userCFSettings: {},
  cfDefalutSettingsDBNode: "default-prod/cf-design-settings"
};

export const fetchCFDefaultSettings = createAsyncThunk(
  "userDesigns/defalutSettings",
  async (statsObj, { rejectWithValue }) => {
    try {
      const response = await fetchUserSavedDesignsData(statsObj);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserCFSettings = createAsyncThunk(
  "userDesigns/fetchUserCFSettings",
  async (statsObj, { rejectWithValue }) => {
    try {
      const response = await fetchUserSavedDesignsData(statsObj);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserDesignSetting = createAsyncThunk(
  "userDesigns/updateUserCFSettings",
  async ({ statsObj, payload }, { rejectWithValue }) => {
    try {
      const response = await updateUserSavedDesignsData(statsObj, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserDesigns = createAsyncThunk(
  "userDesigns/fetch",
  async (statsObj, { rejectWithValue }) => {
    try {
      console.log("fetchUserDesigns statsObj", statsObj);
      const response = await fetchUserSavedDesignsData(statsObj);
      console.log("fetchUserDesigns response", response);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserAccountInfo = createAsyncThunk(
  "userDesigns/account",
  async (statsObj, { rejectWithValue }) => {
    try {
      const response = await fetchUserSavedDesignsData(statsObj);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCFDefaultTemplates = createAsyncThunk(
  "userDesigns/defaultdesigns",
  async (statsObj, { rejectWithValue }) => {
    try {
      const response = await fetchUserSavedDesignsData(statsObj);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserDesign = createAsyncThunk(
  "userDesigns/update",
  async ({ statsObj, payload }, { rejectWithValue }) => {
    try {
      const response = await updateUserSavedDesignsData(statsObj, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteUserDesign = createAsyncThunk(
  "userDesigns/delete",
  async (statsObj, { rejectWithValue }) => {
    try {
      const response = await deleteUserSavedDesignsData(statsObj);
      return response.response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const userDesignsSlice = createSlice({
  name: "userDesigns",
  initialState,
  reducers: {
    resetUserDesignsSlice: (state, action) => {
      return { ...initialState };
    },
    updateUserDesignData: (state, action) => {
      const nudge_data = action.payload;
      
      // ✅ existing logic
      state.data[nudge_data.id] = nudge_data;
      state.user_saved_designs = state.user_saved_designs.map((item) => {
        if (item[0] === nudge_data.id) return [nudge_data.id, nudge_data];
        else return item;
      });

      // ✅ NEW: Also sync editorContent so editor remounts with latest data
      const { grapesjs_content, ...rest } = nudge_data;
      state.editorContent = {
        html: grapesjs_content ? JSON.parse(grapesjs_content) : "",
        data: rest,
      };
    },
    addUserDesignData: (state, action) => {
      let nudge_data = action.payload;
      state.data[nudge_data.id] = nudge_data;
      state.user_saved_designs.unshift([nudge_data.id, nudge_data]);
    },
    deleteUserSavedDesign: (state, action) => {
      let designId = action.payload;
      delete state.data[designId];
      state.user_saved_designs = state.user_saved_designs.filter(
        (item) => item.id !== designId
      );
    },
    setEditorData: (state, action) => {
      const { designId, data_source } = action.payload;
      let allDesigns = state.cfdefaultdesigntemplates;
      if (data_source === "own_design") {
        allDesigns = state.user_saved_designs;
      }

      let matchNudgeData;
      // Check if it's already in [id, data] format or just {id, ...data} format
      if (Array.isArray(allDesigns[0]) && allDesigns[0].length === 2) {
        matchNudgeData = allDesigns.find(([id]) => id === designId);
      } else {
        const foundData = allDesigns.find((item) => item.id === designId);
        if (foundData) {
          matchNudgeData = [foundData.id, foundData]; // Convert to [id, data] format
        }
      }

      if (matchNudgeData) {
        const { grapesjs_content, ...rest } = matchNudgeData[1];
        state.editorContent = {
          html: grapesjs_content ? JSON.parse(grapesjs_content) : "",
          data: rest,
        };
        state.isEditorLive = true;
      } else {
        state.editorContent = { html: "", data: {} };
      }
    },
    openEditor: (state, action) => {
      state.editorContent = { html: "", data: {} };
      state.isEditorLive = true;
    },
    exitFromEditor: (state, action) => {
      state.editorContent = null;
      state.isEditorLive = false;
    },
    setUserCFSettings: (state, action) => {
      state.userCFSettings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCFDefaultSettings.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCFDefaultSettings.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = null;
        state.cfDefalutSettings = Object.values(action.payload)?.[0];
      })
      .addCase(fetchCFDefaultSettings.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload?.error;
      })
      .addCase(fetchUserCFSettings.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUserCFSettings.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = null;
        state.userCFSettings = action.payload;
      })
      .addCase(fetchUserCFSettings.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload;
      })
      .addCase(fetchUserDesigns.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUserDesigns.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = null;
        state.data = action.payload;
        state.user_saved_designs = Object.values(action.payload);
      })
      .addCase(fetchUserDesigns.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload?.error;
      })
      .addCase(updateUserDesign.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateUserDesign.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = null;
      })
      .addCase(updateUserDesign.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload?.error;
      })
      .addCase(deleteUserDesign.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteUserDesign.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = null;
      })
      .addCase(deleteUserDesign.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload?.error;
      })
      .addCase(fetchUserAccountInfo.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUserAccountInfo.fulfilled, (state, action) => {
        state.status = "idle";
        state.websiteURL = action.payload.website;
      })
      .addCase(fetchUserAccountInfo.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload?.error;
      })
      .addCase(fetchCFDefaultTemplates.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCFDefaultTemplates.fulfilled, (state, action) => {
        state.status = "idle";
        state.cfdefaultdesigntemplates = Object.entries(action.payload);
      })
      .addCase(fetchCFDefaultTemplates.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload?.error;
      });
  },
});

export const {
  resetUserDesignsSlice,
  updateUserDesignData,
  addUserDesignData,
  deleteUserSavedDesign,
  setEditorData,
  exitFromEditor,
  openEditor,
  setUserCFSettings,
} = userDesignsSlice.actions;

export const SelectUserDesignsDBData = (state) =>
  state.userDesigns.user_saved_designs;
export const SelectUserDesignsStatus = (state) => state.userDesigns.status;
export const SelectEditorLiveStatus = (state) => state.userDesigns.isEditorLive;
export const SelectEditorContent = (state) => state.userDesigns.editorContent;
export const SelectUserDesignsDBNode = (state) => state.userDesigns.userDesignDBNode;
export const SelectUserWebsiteURL = (state) => state.userDesigns.websiteURL;
export const SelectCFDefalutSettings = (state) => state.userDesigns.cfDefalutSettings;
export const SelectUserCFSettings = (state) => state.userDesigns.userCFSettings;
export const SelectCFDefalutSettingsDBNode = (state) => state.userDesigns.cfDefalutSettingsDBNode;
export const SelectCFDefaultTemplates = (state) =>
  state.userDesigns.defaultTemplatesDBNode;
export const SelectCFDefaultDesignTemplates = (state) =>
  state.userDesigns.cfdefaultdesigntemplates;

export default userDesignsSlice.reducer;
